import { startCase } from "lodash";
import { useHistory } from "react-router";

import { Badge, Icon, Text } from "@alpacahq/ui/old";

import { useGetClientLogo } from "~/v2/api/hooks";
import type { OAuthClientResponse } from "../../api/oauth";
import { Path } from "../../path";

export const DEFAULT_LOGO_HASH = 310052141;

export function getHashFromBlob(blob: string) {
  let hash = 0;

  if (blob.length === 0) {
    return hash;
  }

  for (let i = 0; i < blob.length; i++) {
    const char = blob.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // convert to 32bit integer
  }

  return hash;
}

export const AppCardPlaceholder = () => {
  const history = useHistory();
  const onClick = () => history.push(Path.ROUTE_ALPACA_CONNECT_SUBMIT);

  return (
    <div
      className="group flex h-48 w-full cursor-pointer items-center justify-center rounded-lg border-2 border-cool-gray-300 border-dashed bg-white text-center transition hover:border-cool-gray-400/80"
      onClick={onClick}
    >
      <div className="flex flex-col items-center justify-center space-y-4 text-cool-gray-500 group-hover:text-cool-gray-700">
        <Icon name="PlusCircle" className="h-6 w-6" />
        <Text className="font-medium">Submit Your App</Text>
      </div>
    </div>
  );
};

export const AppCard = ({
  client_id,
  name,
  url,
  description,
  categories,
}: OAuthClientResponse) => {
  const history = useHistory();

  const { logo } = useGetClientLogo({
    clientId: client_id,
    options: {
      enabled: !!client_id,
    },
  });

  const isDefaultLogo = logo && getHashFromBlob(logo) === DEFAULT_LOGO_HASH;

  return (
    <div
      className="flex h-48 w-full cursor-pointer flex-col items-start justify-between space-y-4 rounded-lg border border-cool-gray-300 bg-white shadow-sm transition duration-200 hover:shadow-md"
      onClick={() =>
        history.push(
          Path.format(Path.ROUTE_ALPACA_CONNECT_APP, { clientId: client_id }),
        )
      }
    >
      <div className="flex w-full items-start justify-between px-4 pt-4">
        {logo && !isDefaultLogo ? (
          <img
            className="h-12 w-12 rounded-md"
            src={logo}
            alt={`${name} logo`}
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-cool-gray-200 font-medium text-cool-gray-500 text-xl">
            {(name ?? "").charAt(0).toUpperCase()}
          </div>
        )}
        <Icon
          name="ArrowTopRightOnSquare"
          className="h-[18px] w-[18px] cursor-pointer text-gray-500 transition hover:text-gray-700"
          onClick={(event) => {
            event.stopPropagation();
            window.open(url, "_blank");
          }}
        />
      </div>
      <div className="max-w-full flex-1 px-4">
        <Text className="mb-2 font-medium text-md">{name}</Text>
        <Text className="truncate text-gray-500">{description}</Text>
      </div>
      <div className="space-x-1.5 px-4 pb-4">
        {(Array.isArray(categories) ? categories : [])
          .filter(
            (category): category is string =>
              !!category && typeof category === "string",
          )
          .map((category) => (
            <Badge variant="gray" key={category}>
              {startCase(category.replace(/_/g, " ").toLowerCase())}
            </Badge>
          ))}
      </div>
    </div>
  );
};
